// Equipment load contract — P2-B (docs/implementation/P2_ASSUMED_EQUIPMENT.md).
// Pure helpers over ProcessEquipment + EquipmentConnections rows (db/24) that
// answer "how much heat does this assumed equipment reject into its room?"
// with the CALCULATION BASIS, not just a number. Every downstream consumer
// (today: the Mechanical Systems AHU/plant rollup via hvacLoadEngine's
// equipmentHeatGainByRoomId input) reads this module — the formula lives here
// once and is never duplicated inside an HVAC component.
//
// PRODUCT RULES (resolved 2026-09-12 by the product owner):
//
//   Qty          = total installed units represented by the equipment record.
//   StandbyUnits = installed units intentionally provided as redundant /
//                  non-operating reserve under normal design operation.
//   ActiveUnits  = max(Qty − StandbyUnits, 0)   (central / system equipment;
//                  process / owner equipment normally has StandbyUnits = 0 and
//                  uses the same rule only where the estimator explicitly
//                  identified redundant units).
//   Standby does NOT reduce the calculated building/system demand — it changes
//   the normal operating unit count and per-unit loading. Standby ≠ usage ≠
//   diversity: UsageFactor is the operating load fraction of an ACTIVE unit;
//   diversity / coincidence is a separate system-level concept (P2-E).
//
//   Heat precedence, per connection row:
//     1. explicit HeatGainBTUH when provided                      -> method 'explicit'
//     2. else electrical KW × UsageFactor × 3412.142 BTU/hr per kW -> method 'derived_kw',
//        provenance 'derived_assumed' — the current estimating assumption is
//        that the electrical load rejects its energy into the assigned room
//        (a missing UsageFactor is assumed 1.0 and flagged)
//     3. HP alone is NOT converted to room sensible heat: motor HP is shaft
//        output and does not establish where the energy is ultimately rejected
//        (room air, process fluid, exhaust, duct air, outdoors, another
//        system). HP-only rows return zero derived heat + an unresolved-heat
//        warning. Automating this needs an explicit heat-disposition concept
//        (HeatReleaseFraction / HeatReleaseLocation) — a documented schema gap.
//     4. no heat data -> zero, with the source / warning saying so.
//
// Nothing here reads the DB or React; see src/test/equipmentLoads.test.js.

import { isElectricalType } from "../config/utilityTypes";

export const KW_TO_BTUH = 3412.142;

const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };

// Qty / StandbyUnits -> { qty, standbyUnits, activeUnits }. Qty is NOT NULL
// DEFAULT 1 in the schema; a missing value is read as 1 so a bare record still
// counts as one installed unit.
export function activeUnitsOf(eq) {
  const qty = Math.max(0, num(eq?.Qty) ?? 1);
  const standbyUnits = Math.max(0, num(eq?.StandbyUnits) ?? 0);
  return { qty, standbyUnits, activeUnits: Math.max(qty - standbyUnits, 0) };
}

// One connection row's PER-UNIT room heat and its basis.
//   { btuh, method, provenance, usageFactor, usageFactorAssumed, kw, warnings[] }
//   method:     'explicit' | 'derived_kw' | 'none'
//   provenance: 'explicit' | 'derived_assumed' | 'unresolved' | 'not_specified'
export function connectionHeatGain(conn) {
  const warnings = [];
  const explicit = num(conn?.HeatGainBTUH);
  if (explicit !== null) {
    if (explicit < 0) warnings.push({ code: "heat_negative_clamped", message: `Explicit heat gain ${explicit} BTUH is negative — treated as 0.` });
    return { btuh: Math.max(0, explicit), method: "explicit", provenance: "explicit", usageFactor: null, usageFactorAssumed: false, kw: num(conn.KW), warnings };
  }
  if (!isElectricalType(conn?.UtilityType)) {
    // A steam / CHW / gas service may well reject heat, but nothing about its
    // flow tells us how much or where — only an explicit value counts.
    return { btuh: 0, method: "none", provenance: "not_specified", usageFactor: null, usageFactorAssumed: false, kw: null, warnings };
  }
  const kw = num(conn.KW);
  if (kw !== null && kw > 0) {
    const stored = num(conn.UsageFactor);
    const usageFactor = stored ?? 1;
    const usageFactorAssumed = stored === null;
    if (usageFactorAssumed) warnings.push({ code: "usage_factor_assumed", message: `No usage factor on the ${kw} kW connection — assumed 1.0 (full connected load rejected to the room).` });
    else if (usageFactor < 0 || usageFactor > 1) warnings.push({ code: "usage_factor_out_of_range", message: `Usage factor ${usageFactor} is outside 0–1.` });
    return { btuh: kw * usageFactor * KW_TO_BTUH, method: "derived_kw", provenance: "derived_assumed", usageFactor, usageFactorAssumed, kw, warnings };
  }
  const hp = num(conn.HP);
  if (hp !== null && hp > 0) {
    warnings.push({ code: "heat_unresolved_hp_only", message: `${hp} HP connection has no explicit heat gain or kW — motor HP alone does not establish where the energy is rejected; room heat counted as 0 until an explicit value is entered.` });
    return { btuh: 0, method: "none", provenance: "unresolved", usageFactor: null, usageFactorAssumed: false, kw: null, warnings };
  }
  warnings.push({ code: "heat_not_specified", message: "Electrical connection has no heat gain, kW or HP — room heat counted as 0." });
  return { btuh: 0, method: "none", provenance: "not_specified", usageFactor: null, usageFactorAssumed: false, kw: null, warnings };
}

// One equipment record's heat into its room, with the full basis.
export function equipmentHeatGain(eq, connections = []) {
  const mine = connections.filter(c => c.EquipmentID === eq.EquipmentID);
  const parts = mine.map(c => ({ connectionId: c.ConnectionID ?? null, utilityType: c.UtilityType, circuitDesc: c.CircuitDesc ?? null, ...connectionHeatGain(c) }));
  const explicitBtuhPerUnit = parts.filter(p => p.method === "explicit").reduce((s, p) => s + p.btuh, 0);
  const derivedBtuhPerUnit = parts.filter(p => p.method === "derived_kw").reduce((s, p) => s + p.btuh, 0);
  const perUnitBtuh = explicitBtuhPerUnit + derivedBtuhPerUnit;
  const units = activeUnitsOf(eq);
  const warnings = parts.flatMap(p => p.warnings.map(w => ({ ...w, connectionId: p.connectionId })));
  if (units.activeUnits === 0 && perUnitBtuh > 0) {
    warnings.push({ code: "no_active_units", message: `Qty ${units.qty} with ${units.standbyUnits} standby leaves no operating unit — room heat counted as 0.` });
  }
  const methods = [...new Set(parts.filter(p => p.method !== "none").map(p => p.method))];
  return {
    equipmentId: eq.EquipmentID ?? null,
    ...units,
    explicitBtuhPerUnit, derivedBtuhPerUnit, perUnitBtuh,
    effectiveBtuh: perUnitBtuh * units.activeUnits,
    method: methods.length === 0 ? "none" : methods.length === 1 ? methods[0] : "mixed",
    parts, warnings,
  };
}

// Every room-assigned record rolled up per room. Equipment with only a
// LocationText is outside the takeoff rooms and adds no room heat.
//   { byRoomBtuh: { RoomID: btuh }, byRoom: { RoomID: [breakdown] }, warnings: [{ ...w, equipmentId }] }
// `byRoomBtuh` is exactly the equipmentHeatGainByRoomId map
// hvacLoadEngine.computeAhuZoneRollup already accepts.
// Scenarios Phase J-3: `excludeEquipmentIds` (a Set) keeps a record OUT of the
// room loads — the plant of a non-base design scenario (an alternate's chiller
// in the mechanical room is not in the base design the live loads describe).
// Excluded records are returned in `excluded` with the heat they would have
// added, so the panel can say so instead of silently dropping it.
export function equipmentHeatGainByRoom(equipment = [], connections = [], { excludeEquipmentIds = null } = {}) {
  const byRoomBtuh = {}, byRoom = {}, warnings = [], excluded = [];
  for (const eq of equipment) {
    if (eq.RoomID == null) continue;
    const h = equipmentHeatGain(eq, connections);
    if (excludeEquipmentIds && excludeEquipmentIds.has(eq.EquipmentID)) { excluded.push({ equipmentId: eq.EquipmentID, tag: eq.Tag || eq.Description || `#${eq.EquipmentID}`, roomId: eq.RoomID, btuh: h.effectiveBtuh }); continue; }
    (byRoom[eq.RoomID] ||= []).push(h);
    if (h.effectiveBtuh > 0) byRoomBtuh[eq.RoomID] = (byRoomBtuh[eq.RoomID] || 0) + h.effectiveBtuh;
    warnings.push(...h.warnings.map(w => ({ ...w, equipmentId: eq.EquipmentID })));
  }
  return { byRoomBtuh, byRoom, warnings, excluded };
}
